import * as React from "react";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";

import "./column.styles.scss";

export type HeaderColumnProps = {
  title: string;
  width: number;
  onResizeEnd: (params: { field: DatasheetFieldComponentOriginFieldGetDto }) => void;
  containerRef?: React.RefObject<HTMLElement>;
  field: DatasheetFieldComponentOriginFieldGetDto;
};

export const HeaderColumn = ({ width = 0, onResizeEnd, title, containerRef, field }: HeaderColumnProps) => {
  const resizableElementRef = React.useRef<HTMLDivElement>(null);
  const minColumnWidth = 5;

  const [size, setSize] = React.useState({ width });
  const [resizing, setResizing] = React.useState(false);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(width);

  React.useEffect(() => {
    setSize({ width });
    startWidthRef.current = width;
  }, [width]);

  const handleResizingFinished = () => {
    onResizeEnd({
      field: {
        ...field,
        ...size,
      },
    });
  };

  const handleResize = (e: MouseEvent) => {
    if (!resizing) return;
    const delta = e.clientX - startXRef.current;
    const nextWidth = Math.max(minColumnWidth, startWidthRef.current + delta);
    setSize({ width: nextWidth });
  };

  const resetResizing = React.useCallback(() => {
    setResizing(false);
  }, []);

  const initMouseUp = React.useCallback((e: MouseEvent) => {
    handleResize(e);
    resetResizing();
    handleResizingFinished();
  }, [handleResize, resetResizing, handleResizingFinished]);

  const handleResizing = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = size.width;
    setResizing(true);
  }, [size.width]);

  React.useEffect(() => {
    if (!resizing) {
      return;
    }
    const mouseMoveTarget = containerRef?.current ?? window;
    mouseMoveTarget.addEventListener("mousemove", handleResize as EventListener);
    window.addEventListener("mouseup", initMouseUp as EventListener);

    return () => {
      mouseMoveTarget.removeEventListener("mousemove", handleResize as EventListener);
      window.removeEventListener("mouseup", initMouseUp as EventListener);
    };
  }, [resizing, containerRef, handleResize, initMouseUp]);

  return (
    <div
      className="DatasheetsResultDatasheetTableFieldHeaderColumn"
      style={{ width: size.width }}
    >
      <div
        ref={resizableElementRef}
        className="DatasheetsResultDatasheetTableFieldHeaderColumn__title"
      >
        {title}
      </div>

      <div
        className="DatasheetsResultDatasheetTableFieldHeaderColumn__resizer"
        onMouseDown={handleResizing}
      />
    </div>
  );
};
